import React, { useCallback, useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Drawer from '@groovehq/internal-design-system/lib/components/Drawer/UnmanagedDrawer'
import {
  DRAWER_ID_FOLDERS_CHANNELS_AUTHENTICATE_FAILED,
  DRAWER_ID_FOLDERS_CHANNELS_AUTHENTICATE_SUCCESS,
} from 'ducks/drawers/ids'
import {
  DRAWER_TYPE_CHANNELS_AUTHENTICATE_FAILED,
  DRAWER_TYPE_CHANNELS_AUTHENTICATE_SUCCESS,
} from 'ducks/drawers/types'
import { useDrawer } from 'ducks/drawers/hooks'
import { buildDrawerQueryParam } from 'ducks/drawers/util'
import {
  selectPendingChannelById,
  selectCurrentChannelById,
} from 'ducks/channels/selectors'
import {
  doAddGmailChannel,
  doOauthGmailChannel,
  doAddMessengerChannel,
} from 'ducks/channels/actions'
import { isObject } from 'util/objects'
import ModalBtns from '@groovehq/internal-design-system/lib/components/ModalBtns/ModalBtns'
import HeaderWithImage from '@groovehq/internal-design-system/lib/components/HeaderWithImage/HeaderWithImage'
import { GraphQlValidationError } from 'util/global-exceptions'
import { CHANNEL_TYPES, drawerConfig } from '../Channels.data'
import { styles } from './styles'
import useSourceWording from './useSourceWording'

const { DEFAULT_DRAWER_TITLE } = drawerConfig

const drawerSuccessConfig = {
  id: DRAWER_ID_FOLDERS_CHANNELS_AUTHENTICATE_SUCCESS,
  type: DRAWER_TYPE_CHANNELS_AUTHENTICATE_SUCCESS,
  closeIgnoresStack: true,
}

const drawerFailedConfig = {
  id: DRAWER_ID_FOLDERS_CHANNELS_AUTHENTICATE_FAILED,
  type: DRAWER_TYPE_CHANNELS_AUTHENTICATE_FAILED,
  closeIgnoresStack: true,
}

const AuthenticateChannel = ({
  onClose,
  drawerResourceId: channelId,
  drawerChannelType: channelType,
  drawerRetryAuth: retryAuth,
  drawerSource: source = 'settings',
  previousDrawer,
}) => {
  const dispatch = useDispatch()
  const pendingChannel = useSelector(state =>
    selectPendingChannelById(state, channelId)
  )
  const currentChannel = useSelector(state =>
    selectCurrentChannelById(state, channelId)
  )

  const [nextDrawerConfig, setNextDrawerConfig] = useState(drawerSuccessConfig)
  const [createdMailbox, setCreatedMailbox] = useState(null)
  const [errorCode, setErrorCode] = useState(null)

  const channel = pendingChannel || currentChannel
  const { email: channelEmail } = channel
  const channelTypeConfig = CHANNEL_TYPES[channelType] || {}
  const { name, headerImg } = channelTypeConfig
  const channelTitle = channelEmail || name
  const {
    drawerTitle: sourceDrawerTitle,
    title: sourceTitle,
    intro: sourceIntro,
  } = useSourceWording(source)

  const { drawerId: nextDrawerId, openDrawer: openNextDrawer } = useDrawer(
    nextDrawerConfig
  )

  const handleError = useCallback(
    error => {
      // Kevin R (2021-12-08)
      // Good god, how can we have gotten error handling so wrong.
      // We need to refactor our graphql and oauth dialogs to return proper
      // error objects so that we can use a simple "instanceof" check to
      // determine what type of error has occurred.
      if (isObject(error)) {
        if (error instanceof GraphQlValidationError) {
          if (channelType === 'messenger') {
            setErrorCode('ERR_CREATE_WIDGET')
          } else {
            setErrorCode('ERR_CREATE_MAILBOX')
          }
        } else if (error.errors) {
          const message = error.errors[0].message

          if (message.includes('CreateMailbox')) {
            setErrorCode('ERR_CREATE_MAILBOX')
          } else if (message.includes('CreateWidget')) {
            setErrorCode('ERR_CREATE_WIDGET')
          } else if (message.includes('OAuthAuthenticationUrl')) {
            setErrorCode('ERR_FETCH_OAUTH_URL')
          }
        }
        // The following error can be returned from the gmail oauth process
        // Refresh token missing. Please remove Groove from your authorized applications at: https://security.google.com/settings/security/permissions and then link your mailbox again.
      } else if (
        Object.keys(CHANNEL_TYPES).includes(channelType) &&
        error.includes('remove') &&
        error.includes('authorized') &&
        error.includes('applications')
      ) {
        setErrorCode('ALREADY_LINKED')
      } else if (error === 'ERR_USER_CLOSED_POPUP') {
        setErrorCode(error)
      }
      // eslint-disable-next-line no-console
      console.error(error)

      setNextDrawerConfig(drawerFailedConfig)
      setCreatedMailbox(false)
    },
    [channelType]
  )

  const handleGmailChannel = useCallback(
    async () => {
      try {
        setCreatedMailbox(null)
        setErrorCode(null)
        const mailbox =
          source === 'inbox'
            ? await dispatch(doOauthGmailChannel(channelId))
            : await dispatch(doAddGmailChannel(pendingChannel))
        setNextDrawerConfig(drawerSuccessConfig)
        setCreatedMailbox(mailbox)
      } catch (error) {
        handleError(error)
      }
    },
    [dispatch, pendingChannel, handleError, source, channelId]
  )

  const handleMessengerChannel = useCallback(
    async () => {
      try {
        setCreatedMailbox(null)
        setErrorCode(null)
        const widget = await dispatch(doAddMessengerChannel(pendingChannel))
        setNextDrawerConfig(drawerSuccessConfig)
        setCreatedMailbox(widget)
      } catch (error) {
        handleError(error)
      }
    },
    [dispatch, pendingChannel, handleError]
  )

  const handleOnClick = useCallback(
    () => {
      if (channelType === 'gmail') {
        handleGmailChannel()
      } else if (channelType === 'messenger') {
        handleMessengerChannel()
      }
    },
    [channelType, handleGmailChannel, handleMessengerChannel]
  )

  useEffect(
    () => {
      if (createdMailbox === null) return
      const query = {
        ...buildDrawerQueryParam(
          nextDrawerId,
          'drawerChannelType',
          channelType
        ),
      }
      if (errorCode) {
        Object.assign(
          query,
          buildDrawerQueryParam(nextDrawerId, 'drawerAuthErrorCode', errorCode)
        )
      }
      if (source) {
        Object.assign(
          query,
          buildDrawerQueryParam(nextDrawerId, 'drawerSource', source)
        )
      }
      openNextDrawer(createdMailbox?.id, {
        clearStack: !!createdMailbox,
        query,
      })
    },
    // We only want to open the net drawer when the createdMailbox changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openNextDrawer, createdMailbox]
  )

  useEffect(
    () => {
      if (retryAuth === 1) {
        handleOnClick()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [retryAuth]
  )

  const handleBack = useCallback(
    () => {
      onClose({ ignoreStack: false })
    },
    [onClose]
  )

  const footer = (
    <ModalBtns
      saveBtnText={`Authenticate ${name}`}
      onSave={handleOnClick}
      tertiaryBtnText={previousDrawer && 'Back'}
      onClickTertiaryBtn={handleBack}
    />
  )

  return (
    <Drawer
      title={sourceDrawerTitle || DEFAULT_DRAWER_TITLE}
      onClose={onClose}
      footer={footer}
    >
      <HeaderWithImage
        className="grui mt-5 mb-12"
        img={headerImg}
        css={styles.header}
        alt="Mailbox"
        title={sourceTitle || `Authenticate ${name}`}
        intro={
          sourceIntro ||
          `To start receiving your ${channelTitle} conversations in Groove, you will need to authenticate your ${name} account.`
        }
      />
    </Drawer>
  )
}

export default AuthenticateChannel
